import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import type { PlanReviewSummary } from "@/api/plan_review";
import {
  completenessPillClass,
  completenessPillLabel,
} from "@/components/plan_review/planReviewDisplay";

export type PlanReviewSummaryDialogSection = "checklist_items" | "extracted_facts" | "detected_issues";

type PlanReviewResultsSummaryProps = {
  reviewComplete: boolean;
  summary: PlanReviewSummary | undefined;
  checklistResultsLength: number;
  dimensionalFactsLength: number;
  issuesLength: number;
  completedRunTimeLabel: string | null;
  onSummaryDialog: (section: PlanReviewSummaryDialogSection) => void;
  onGoToZoning: () => void;
};

function SummaryMetricCards({
  reviewComplete,
  summary,
  checklistResultsLength,
  dimensionalFactsLength,
  issuesLength,
  onSummaryDialog,
}: Pick<
  PlanReviewResultsSummaryProps,
  | "reviewComplete"
  | "summary"
  | "checklistResultsLength"
  | "dimensionalFactsLength"
  | "issuesLength"
  | "onSummaryDialog"
>) {
  return (
    <div className={`grid gap-3 ${summary?.completeness ? "sm:grid-cols-2 lg:grid-cols-4" : "lg:grid-cols-3"}`}>
      {summary?.completeness && (
        <div className="text-left">
          <Card className="h-full border-slate-200 p-3">
            <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Plan Completeness</div>
            <div className="mt-1.5 flex items-center gap-2">
              <span className="text-xl font-semibold text-slate-900">{summary.completeness.score}%</span>
              <span
                className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${completenessPillClass(summary.completeness.pass)}`}
              >
                {completenessPillLabel(summary.completeness.pass)}
              </span>
            </div>
            <div className="mt-1.5 text-xs text-slate-600">
              {summary.completeness.required_total === 0
                ? "No required items"
                : `${summary.completeness.required_present} of ${summary.completeness.required_total} required present · ${summary.completeness.required_uncertain} uncertain`}
            </div>
          </Card>
        </div>
      )}
      {reviewComplete ? (
        <button type="button" className="text-left" onClick={() => onSummaryDialog("checklist_items")}>
          <Card className="h-full p-3 transition hover:border-blue-200 hover:shadow-sm">
            <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Checklist Items</div>
            <div className="mt-1.5 text-xl font-semibold text-slate-900">
              {summary?.checklist_items.total || checklistResultsLength}
            </div>
            <div className="mt-1.5 text-xs text-slate-600">
              {summary?.checklist_items.present || 0} present, {summary?.checklist_items.uncertain || 0} uncertain,{" "}
              {summary?.checklist_items.missing || 0} missing, {summary?.checklist_items.edited || 0} edited
            </div>
          </Card>
        </button>
      ) : (
        <div className="text-left">
          <Card className="h-full border-slate-200 p-3">
            <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Checklist Items</div>
            <div className="mt-1.5 text-xl font-semibold text-slate-900">
              {summary?.checklist_items.total || checklistResultsLength}
            </div>
            <div className="mt-1.5 text-xs text-slate-600">
              {summary?.checklist_items.present || 0} present, {summary?.checklist_items.uncertain || 0} uncertain,{" "}
              {summary?.checklist_items.missing || 0} missing, {summary?.checklist_items.edited || 0} edited
            </div>
          </Card>
        </div>
      )}
      {reviewComplete ? (
        <button type="button" className="text-left" onClick={() => onSummaryDialog("extracted_facts")}>
          <Card className="h-full p-3 transition hover:border-blue-200 hover:shadow-sm">
            <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Extracted Facts</div>
            <div className="mt-1.5 text-xl font-semibold text-slate-900">
              {summary?.extracted_facts.total || dimensionalFactsLength}
            </div>
            <div className="mt-1.5 text-xs text-slate-600">
              {summary?.extracted_facts.evidence_matches || 0} evidence matches across{" "}
              {summary?.extracted_facts.pages_with_evidence || 0} pages
            </div>
          </Card>
        </button>
      ) : (
        <div className="text-left">
          <Card className="h-full border-slate-200 p-3">
            <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Extracted Facts</div>
            <div className="mt-1.5 text-xl font-semibold text-slate-900">
              {summary?.extracted_facts.total || dimensionalFactsLength}
            </div>
            <div className="mt-1.5 text-xs text-slate-600">
              {summary?.extracted_facts.evidence_matches || 0} evidence matches across{" "}
              {summary?.extracted_facts.pages_with_evidence || 0} pages
            </div>
          </Card>
        </div>
      )}
      {reviewComplete ? (
        <button type="button" className="text-left" onClick={() => onSummaryDialog("detected_issues")}>
          <Card className="h-full p-3 transition hover:border-blue-200 hover:shadow-sm">
            <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Detected Issues</div>
            <div className="mt-1.5 text-xl font-semibold text-slate-900">{summary?.detected_issues.total || issuesLength}</div>
            <div className="mt-1.5 text-xs text-slate-600">
              {summary?.detected_issues.missing_required || 0} missing required,{" "}
              {summary?.detected_issues.uncertain_required || 0} uncertain required
            </div>
          </Card>
        </button>
      ) : (
        <div className="text-left">
          <Card className="h-full border-slate-200 p-3">
            <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Detected Issues</div>
            <div className="mt-1.5 text-xl font-semibold text-slate-900">{summary?.detected_issues.total || issuesLength}</div>
            <div className="mt-1.5 text-xs text-slate-600">
              {summary?.detected_issues.missing_required || 0} missing required,{" "}
              {summary?.detected_issues.uncertain_required || 0} uncertain required
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

export function PlanReviewResultsSummary({
  reviewComplete,
  summary,
  checklistResultsLength,
  dimensionalFactsLength,
  issuesLength,
  completedRunTimeLabel,
  onSummaryDialog,
  onGoToZoning,
}: PlanReviewResultsSummaryProps) {
  const [breakdownOpen, setBreakdownOpen] = useState(false);

  if (!reviewComplete) {
    return (
      <SummaryMetricCards
        reviewComplete={reviewComplete}
        summary={summary}
        checklistResultsLength={checklistResultsLength}
        dimensionalFactsLength={dimensionalFactsLength}
        issuesLength={issuesLength}
        onSummaryDialog={onSummaryDialog}
      />
    );
  }

  const checklist = summary?.checklist_items;
  const issueTotal = summary?.detected_issues.total ?? issuesLength;

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0 space-y-0.5">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-slate-800">
            {summary?.completeness ? (
              <>
                <span className="font-semibold">{summary.completeness.score}%</span>
                <span
                  className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${completenessPillClass(summary.completeness.pass)}`}
                >
                  {completenessPillLabel(summary.completeness.pass)}
                </span>
                <span className="text-slate-300" aria-hidden="true">
                  ·
                </span>
              </>
            ) : null}
            <span>
              {checklist?.present ?? 0} met · {checklist?.uncertain ?? 0} verify · {checklist?.missing ?? 0} missing
            </span>
            {issueTotal > 0 ? (
              <>
                <span className="text-slate-300" aria-hidden="true">
                  ·
                </span>
                <span>{issueTotal} issues</span>
              </>
            ) : null}
          </div>
          {completedRunTimeLabel ? (
            <div className="text-xs text-slate-500">Completed in {completedRunTimeLabel}</div>
          ) : null}
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setBreakdownOpen((open) => !open)}
            data-testid="plan-review-results-breakdown-toggle"
          >
            {breakdownOpen ? "Hide breakdown" : "Show breakdown"}
          </Button>
          <Button size="sm" onClick={onGoToZoning}>
            Check against zoning code
          </Button>
        </div>
      </div>

      {breakdownOpen ? (
        <SummaryMetricCards
          reviewComplete={reviewComplete}
          summary={summary}
          checklistResultsLength={checklistResultsLength}
          dimensionalFactsLength={dimensionalFactsLength}
          issuesLength={issuesLength}
          onSummaryDialog={onSummaryDialog}
        />
      ) : null}
    </div>
  );
}
